import React, { useRef, useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import moment from 'moment'

import styles from './styles.module.scss'

import { getEmployeeDocuments } from '../../../api'
import { useQuery } from '../../../hooks/useQuery'
import { showDocumentForm, showDocumentPreview } from '../../../store/documents/actions'
import usePermissions from '../../Core/usePermissions'

import { BackIcon, PlusIcon } from '../Icons'

const getStatusColors = (status) => {
  const value = (status || '').toLowerCase()
  if (value.includes('valid') || value.includes('acknowledged') || value.includes('approved') || value.includes('signed')) {
    return { background: '#D1F9CB', text: '#32BF1F', border: '#BEF3B7' }
  }
  if (value.includes('seen')) {
    return { background: '#ECF0FF', text: '#6D75E8', border: '#D3D6FA' }
  }
  if (value.includes('pending') || value.includes('review') || value.includes('awaiting')) {
    return { background: '#FFD4A5', text: '#D77000', border: '#E7C88C' }
  }
  if (value.includes('declined') || value.includes('overdue') || value.includes('expired')) {
    return { background: '#FFBDBD', text: '#D70000', border: '#F77C7C' }
  }
  return { background: '#E9E9E9', text: '#919191', border: '#C6C6C6' }
}

const getExpirationColor = (label) => {
  if (!label) return '#808F94'
  if (label.toLowerCase().startsWith('expired')) return '#DE4343'
  if (label.toLowerCase().startsWith('expires')) return '#D77000'
  return '#808F94'
}

const getDocumentSubtitle = (t, document) => {
  const parts = []
  if (document.effective_date) {
    parts.push(t('Effective {{date}}', { date: moment(document.effective_date).format('MMM D, YYYY') }))
  }
  if (document.due_date_for_action) {
    parts.push(t('due {{date}}', { date: moment(document.due_date_for_action).format('MMM D, YYYY') }))
  }
  return parts.join(' · ')
}

const getDocumentActionLabel = (t, document) => {
  if (document.signed_at) {
    return t('Signed {{date}}', { date: moment(document.signed_at).format('MMM D, HH:mm') })
  }
  if (document.approved_at) {
    return t('Approved {{date}}', { date: moment(document.approved_at).format('MMM D, HH:mm') })
  }
  if (document.acknowledged_at) {
    return t('Acknowledged {{date}}', { date: moment(document.acknowledged_at).format('MMM D, HH:mm') })
  }
  return null
}

const EmployeeDocuments = () => {
  const history = useHistory()
  const { employee_id: employeeId } = useQuery(['employee_id'])
  const { id: companyId } = useParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const permissions = usePermissions([{
    name: 'can_upload_documents',
    permission: 'documents_can_upload_documents'
  }])

  const [{employee, categories}, setData] = useState({employee: {}, categories: []})


  const headerRef = useRef()

  useEffect(() => {
    if (companyId && employeeId) {
      init(companyId, employeeId)
    }
  }, [companyId, employeeId])

  const init = (companyId, employeeId) => {
    getEmployeeDocuments(companyId, employeeId).then(res => {
      if (res) {
        setData(res)
      }
    })
  }

  const handleCreateDocument = () => {
    dispatch(showDocumentForm())
  }

  const handleOpenDocument = (document) => () => {
    dispatch(showDocumentPreview({documentId: document.id, employeeId: employeeId}))
  }

  return (
    <div className={styles.container}>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.backButton} onClick={history.goBack}>
          <BackIcon />
        </div>
        <div>
          <div className={styles.headerTitle}>{employee.name} {employee.surname || ''}</div>
          <div className={styles.headerDesc}>{`${employee.company_role ? `${employee.company_role} · ` : ``}${employee.email} ${employee.place ? `· ${employee.place}` : ''}`}</div>
        </div>
        <div className={styles.headerActions}>
          {
            permissions.can_upload_documents
              ? <button type="button" className={styles.button} onClick={handleCreateDocument}>
                  <PlusIcon />
                  { t('Upload document') }
                </button>
              : null
          }
        </div>
      </div>
      <div className={styles.content}>
        {
          categories.map(group => (
            <div key={group.category_id} className={styles.group}>
              <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>{ group.name }</div>
                <div className={styles.groupCount}>{ group.count }</div>
              </div>
              {
                group.documents.map(document => {
                  const statusColors = getStatusColors(document.status.value)
                  const actionLabel = getDocumentActionLabel(t, document)

                  return (
                    <div key={document.document_id} className={styles.row} onClick={handleOpenDocument(document)}>
                      <div className={styles.rowInfo}>
                        <div className={styles.rowTitle}>{ document.name }</div>
                        <div className={styles.rowSubtitle}>{ getDocumentSubtitle(t, document) }</div>
                      </div>
                      <div className={styles.rowMeta}>
                        <div style={{ color: getExpirationColor(document.expiration_label) }}>
                          { document.expiration_label || '—' }
                        </div>
                        {
                          actionLabel ? <div className={styles.rowActionLabel}>{ actionLabel }</div> : null
                        }
                      </div>
                      <div
                        className={styles.statusBadge}
                        style={{ backgroundColor: statusColors.background, color: statusColors.text, border: `1px solid ${statusColors.border}` }}
                      >
                        <div className={styles.statusDot} style={{ backgroundColor: statusColors.text }} />
                        { document.status.label }
                      </div>
                    </div>
                  )
                })
              }
            </div>
          ))
        }
      </div>
    </div>
  )
}

export default EmployeeDocuments
